import { useEffect, useState } from 'react';
import { podeFazer } from '../../acesso';
import type { Eu } from '../../acesso';
import type { Sessao } from '../../auth';
import { EstadoVazio } from '../../design/Estados';
import { Icone } from '../../design/Icone';
import { VeCusto } from '../../design/sigilo';
import type { OfertaDaDistribuidora, ProdutoAoVivo, ProdutoDaConsulta } from '../../encomendas';
import '../central/central.css';
import './encomendas.css';
import './fluxo.css';
import {
  AuditoriaDeEncomendas,
  ConfiguracoesDeEncomendas,
  PainelDeEncomendas,
  VinculosParaRevisar,
} from './Admin';
import { Consulta } from './Consulta';
import { Encomendar } from './Encomendar';
import { FaltasRegistradas } from './Faltas';
import { ListaDeEncomendas } from './ListaDeEncomendas';
import type { FiltroDaLista } from './ListaDeEncomendas';
import { Recebimento } from './Recebimento';

/**
 * ENCOMENDAS (dono, 02/10/2026) — uma entrada na sidebar e a subnav com as áreas que a SESSÃO
 * pode ver. A subnav só esconde: quem recusa é o servidor (cada rota exige a capacidade).
 *
 * "Nenhuma encomenda pode ser esquecida": a consulta encomenda, Minhas/Todas acompanham, o
 * Recebimento fecha o ciclo e o admin vê o que precisa de atenção.
 */

export type AreaDeEncomendas =
  | 'encomendas'
  | 'encomendas-minhas'
  | 'encomendas-todas'
  | 'encomendas-revisar'
  | 'encomendas-recebimento'
  | 'encomendas-faltas'
  | 'encomendas-config'
  | 'encomendas-auditoria';

type GrupoDasAreas = 'Balcão' | 'Acompanhar' | 'Chegada' | 'Gestão';

/** Ordem e grupos da subnav (dono 14/10: "completas e organizadas"). */
const AREAS: readonly {
  rota: AreaDeEncomendas;
  rotulo: string;
  capacidade: string;
  grupo: GrupoDasAreas;
}[] = [
  { rota: 'encomendas', rotulo: 'Consulta', capacidade: 'encomendas.consultar', grupo: 'Balcão' },
  {
    rota: 'encomendas-minhas',
    rotulo: 'Minhas encomendas',
    capacidade: 'encomendas.ver_proprias',
    grupo: 'Balcão',
  },
  {
    rota: 'encomendas-faltas',
    rotulo: 'Faltas',
    capacidade: 'encomendas.ver_faltas',
    grupo: 'Balcão',
  },
  {
    rota: 'encomendas-todas',
    rotulo: 'Todas as encomendas',
    capacidade: 'encomendas.ver_todas',
    grupo: 'Acompanhar',
  },
  {
    rota: 'encomendas-revisar',
    rotulo: 'Vínculos para revisar',
    capacidade: 'encomendas.vincular',
    grupo: 'Acompanhar',
  },
  {
    rota: 'encomendas-recebimento',
    rotulo: 'Recebimento e notas',
    capacidade: 'encomendas.receber',
    grupo: 'Chegada',
  },
  {
    rota: 'encomendas-config',
    rotulo: 'Configurações',
    capacidade: 'encomendas.configurar',
    grupo: 'Gestão',
  },
  {
    rota: 'encomendas-auditoria',
    rotulo: 'Auditoria',
    capacidade: 'encomendas.ver_todas',
    grupo: 'Gestão',
  },
];

export function areasVisiveis(eu: Eu | null) {
  return AREAS.filter((a) => podeFazer(eu, a.capacidade));
}

export function Encomendas({
  eu,
  sessao,
  area,
}: {
  eu: Eu | null;
  sessao: Sessao;
  area: AreaDeEncomendas;
}) {
  const visiveis = areasVisiveis(eu);
  const atual = visiveis.find((a) => a.rota === area) ?? null;
  const [encomendando, setEncomendando] = useState<{
    produto: ProdutoDaConsulta;
    aoVivo: ProdutoAoVivo | null;
    oferta: OfertaDaDistribuidora;
  } | null>(null);
  const [criada, setCriada] = useState<{ codigo: string } | null>(null);
  // o aviso de "salva" é da Consulta: trocar de área o fecha
  useEffect(() => setCriada(null), [area]);
  // celular: a subnav rola de lado — leva a aba aberta para a vista
  useEffect(() => {
    document
      .querySelector('.enc-subnav .cv-sel')
      ?.scrollIntoView({ inline: 'center', block: 'nearest' });
  }, [area]);
  const [filtroDoPainel, setFiltroDoPainel] = useState<FiltroDaLista>('todas');
  const podeCriar = podeFazer(eu, 'encomendas.criar');
  const podeVincular = podeFazer(eu, 'encomendas.vincular');

  let conteudo;
  switch (atual?.rota) {
    case 'encomendas':
      conteudo = (
        <Consulta
          {...(podeFazer(eu, 'encomendas.registrar_falta') ? { registrarFalta: { sessao } } : {})}
          {...(podeCriar
            ? {
                aoEncomendar: (
                  produto: ProdutoDaConsulta,
                  aoVivo: ProdutoAoVivo | null,
                  oferta: OfertaDaDistribuidora,
                ) => {
                  setCriada(null);
                  setEncomendando({ produto, aoVivo, oferta });
                },
              }
            : {})}
        />
      );
      break;
    case 'encomendas-minhas':
      conteudo = <ListaDeEncomendas sessao={sessao} modo="minhas" podeVincular={false} />;
      break;
    case 'encomendas-todas':
      conteudo = (
        <>
          <PainelDeEncomendas aoFiltrar={setFiltroDoPainel} />
          <ListaDeEncomendas
            sessao={sessao}
            modo="todas"
            podeVincular={podeVincular}
            filtroInicial={filtroDoPainel}
          />
        </>
      );
      break;
    case 'encomendas-revisar':
      conteudo = <VinculosParaRevisar sessao={sessao} />;
      break;
    case 'encomendas-recebimento':
      conteudo = <Recebimento sessao={sessao} eu={eu} />;
      break;
    case 'encomendas-faltas':
      conteudo = <FaltasRegistradas sessao={sessao} />;
      break;
    case 'encomendas-config':
      conteudo = <ConfiguracoesDeEncomendas sessao={sessao} />;
      break;
    case 'encomendas-auditoria':
      conteudo = <AuditoriaDeEncomendas />;
      break;
    default:
      conteudo = (
        <EstadoVazio
          icone="lock"
          titulo="Esta área não faz parte do seu acesso"
          texto="Peça a um administrador se você precisa dela."
        />
      );
  }

  // Dono 03/10: no módulo, o ADMIN vê tudo (custo, margem); o balconista só PMC e venda do HOS.
  // O servidor decide o mesmo (sigilo-da-loja.ts); a tela só deixa de esconder a linha.
  const veCustoAqui = podeFazer(eu, 'loja.ver') || podeFazer(eu, 'encomendas.ver_todas');
  return (
    <VeCusto.Provider value={veCustoAqui}>
      <div className="enc">
        {visiveis.length > 1 && (
          <nav className="cv-subnav enc-subnav" aria-label="Encomendas">
            {visiveis.map((a, k) => (
              <span key={a.rota} className="cv-subnav-item">
                {/* o rótulo do grupo só aparece quando há mais de um grupo visível */}
                {new Set(visiveis.map((x) => x.grupo)).size > 1 &&
                  visiveis[k - 1]?.grupo !== a.grupo && (
                    <span className={`cv-subnav-grupo${k === 0 ? ' enc-grupo-primeiro' : ''}`}>
                      {a.grupo.toUpperCase()}
                    </span>
                  )}
                <a
                  href={`#/${a.rota}`}
                  className={area === a.rota ? 'cv-sel' : ''}
                  aria-current={area === a.rota ? 'page' : undefined}
                >
                  {a.rotulo}
                </a>
              </span>
            ))}
          </nav>
        )}
        {criada && (
          <div className="enc-sucesso" role="status">
            <Icone nome="check" tamanho={18} traco={2.2} />
            <span>
              Encomenda <b>{criada.codigo}</b> salva. Ela já aparece em Minhas encomendas.
            </span>
            <a href="#/encomendas-minhas" className="enc-botao-sec">
              Ver minhas encomendas
            </a>
            <button type="button" className="enc-link" onClick={() => setCriada(null)}>
              Fechar
            </button>
          </div>
        )}
        {conteudo}
        {encomendando && (
          <Encomendar
            sessao={sessao}
            produto={encomendando.produto}
            aoVivo={encomendando.aoVivo}
            oferta={encomendando.oferta}
            aoFechar={() => setEncomendando(null)}
            aoCriar={(r) => {
              setEncomendando(null);
              setCriada({ codigo: r.codigo });
            }}
          />
        )}
      </div>
    </VeCusto.Provider>
  );
}
